import React, { useCallback, useContext, useEffect, useState } from 'react';
import constants from '../../../common/constants';
import { Alert, Box, SpaceBetween } from '@amzn/awsui-components-core';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import {
  PublicationIssueType,
  QuestionnaireResponses,
  QuestionResponse,
  WorkflowEventTypes,
} from '@amzn/awscat-pattern-library-service-model';
import { AppContext, withAppContext } from '../../../contexts/AppContext';
import {
  COMMENT_MAX,
  createQuestionnaireResponses,
  isScoreValid,
  REVIEW_COMMENT_MAX,
} from '../../utils/ContentQualityUtils';
import RouterPaths from '../../../common/RouterPaths';
import BrowserHistory from '../../utils/BrowserHistory';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import FlashbarMessageBroker from '../../../common/FlashbarMessageBroker';

interface Props {
  contentId: string;
  workflowId: number;
  draftBarRaiserId: string | undefined;
  idToSequenceMap: Map<string, number>;
  sequenceToResponseMap: Map<number, QuestionResponse>;
  score: number;
  reviewComment: string;
  handleReviewComplete: () => void;
  contentMainTechDomain: string;
  isUnreviewedPublishedContent?: boolean;
  unpublishRecommended?: boolean;
}

const BarRaiserControls: React.FunctionComponent<Props> = (props: Props) => {
  const {
    contentId,
    workflowId,
    idToSequenceMap,
    sequenceToResponseMap,
    score,
    reviewComment,
    handleReviewComplete,
    contentMainTechDomain,
    isUnreviewedPublishedContent,
  } = props;

  const mplService: IMplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker = FlashbarMessageBroker.create();

  const [overallComment, setOverallComment] = useState<string>(reviewComment);
  const [questionnaireError, setQuestionnaireError] = useState<string>('');
  const [reviewCommentEmptyError, setReviewCommentEmptyError] = useState<string>('');
  const [reviewCommentSizeError, setReviewCommentSizeError] = useState<string>('');
  const [responseCommentSizeError, setResponseCommentSizeError] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [showQuestionError, setShowQuestionError] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const { authnInfo } = appContext;

  if (!authnInfo) {
    throw new Error('It is expected that the user is logged in when using this control');
  }

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const validateReviewCommentError = useCallback(() => {
    let error = '';

    if (reviewComment) {
      if (reviewComment.length > REVIEW_COMMENT_MAX) {
        error = constants.BAR_RAISER_CONTROL_FEEDBACK_TOO_LONG_ERROR;
      }
      setReviewCommentEmptyError('');
    } else if (isUnreviewedPublishedContent) {
      error = constants.BAR_RAISER_CONTROL_FEEDBACK_MISSING_ERROR;
    }

    setReviewCommentSizeError(error);
  }, [reviewComment, isUnreviewedPublishedContent]);

  useEffect(() => {
    setOverallComment(reviewComment);
    validateReviewCommentError();
    setShowError(false);
    setShowQuestionError(false);
  }, [reviewComment, validateReviewCommentError]);

  useEffect(() => {
    const validateQuestionnaireError = () => {
      if (!isScoreValid(score)) {
        setQuestionnaireError(
          isUnreviewedPublishedContent
            ? constants.BAR_RAISER_CONTROL_MISSING_PUBLISHED_RESPONSE_ERROR
            : constants.BAR_RAISER_CONTROL_MISSING_RESPONSE_ERROR,
        );
      } else {
        setQuestionnaireError('');
      }
    };

    validateQuestionnaireError();
    validateReviewCommentError();
    setShowError(false);
    setShowQuestionError(false);
  }, [score, validateReviewCommentError, isUnreviewedPublishedContent]);

  useEffect(() => {
    validateResponseCommentError(sequenceToResponseMap);
    setShowError(false);
    setShowQuestionError(false);
  }, [sequenceToResponseMap]);

  const validateResponseCommentError = (
    questionnaireResponseMap: Map<number, QuestionResponse>,
  ) => {
    setResponseCommentSizeError('');
    for (const entry of questionnaireResponseMap.entries()) {
      const [sequence, questionnaire] = entry;
      const { response, comment } = questionnaire;
      if (response === 'NO' && comment.length > COMMENT_MAX) {
        setResponseCommentSizeError(constants.BAR_RAISER_CONTROL_COMMENT_TOO_LONG_ERROR);
        break;
      }
    }
  };

  const sendWorkflowEvent = async (
    eventType: WorkflowEventTypes,
    responses: QuestionnaireResponses,
    qualityScore: number,
    prevWorkflowId?: number,
  ) => {
    await createWorkflowEventWithStatePolling(
      contentId,
      workflowId,
      eventType,
      overallComment,
      undefined,
      responses,
      qualityScore,
      { prevWorkflowId },
    );
  };

  const getAndParseContentMainTechDomain = () => {
    // e.g., CloudFoundations -> cloud-foundations
    const formatTechDomainToURL: string = contentMainTechDomain
      .replace(/([a-zA-Z])(?=[A-Z])/g, '$1-')
      .toLowerCase();
    return formatTechDomainToURL;
  };

  const navigateToBarRaiserDashboard = async (forPublishedContents?: boolean) => {
    const mainTechDomain = getAndParseContentMainTechDomain();
    BrowserHistory.push(RouterPaths.BAR_RAISER_DASHBOARD_PREFIX + `-${mainTechDomain}`, {
      keepFlashbarMessages: true,
      activeTabId: forPublishedContents ? 'br-published-contents' : undefined,
    });
  };

  const validateReject = async () => {
    if (!overallComment) {
      setReviewCommentEmptyError(constants.BAR_RAISER_CONTROL_MISSING_DRAFT_FEEDBACK_ERROR);
      setShowError(true);
      setShowQuestionError(false);
      return false;
    } else {
      setReviewCommentEmptyError('');
    }
    // BR can reject content without filling all questions in questionnaire
    if (!!reviewCommentSizeError || !!responseCommentSizeError) {
      setShowError(true);
      setShowQuestionError(false);
      return false;
    }
    return true;
  };

  const rejectWorkflow = async () => {
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };
    try {
      // reject publication — the service stores the questionnaire responses carried by the event
      await sendWorkflowEvent(WorkflowEventTypes.REJECT, evaluation, score);
    } finally {
      handleReviewComplete();
    }
    await navigateToBarRaiserDashboard();
  };

  const validateCompleteReview = async () => {
    if (!!reviewCommentSizeError || !!questionnaireError || !!responseCommentSizeError) {
      setShowError(true);
      setShowQuestionError(true);
      return false;
    }
    return true;
  };

  const publishWorkflow = async () => {
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };
    try {
      // publish — the service stores the questionnaire responses carried by the event
      await sendWorkflowEvent(WorkflowEventTypes.PUBLISH, evaluation, score);
    } finally {
      handleReviewComplete();
    }
    await navigateToBarRaiserDashboard();
  };

  // when I (the bar raiser) review published contents that have never been
  // bar-raised before (the isReviewingPublishedContent prop is true) or have been recommended to unpublish by another bar raiser, I must
  // then assign myself as the bar raiser.
  //
  // This applies to both the current version (the one being reviewed) and the
  // next version (draft) to make sure the bar raiser is displayed correctly
  // in the UI.
  const completeReviewingNonBarRaisedContent = async () => {
    if (!isUnreviewedPublishedContent && !props.unpublishRecommended) {
      throw new Error('this function is only applicable when reviewing published contents');
    }

    // As explained above, I should be the bar raiser of the next version,
    // unless it's already have a bar raiser:
    if (!props.draftBarRaiserId) {
      await mplService.assignBarRaiser(contentId, workflowId, authnInfo.userProfile.userId);
    }

    // As I'm the one who approves the current version, I should also be its bar raiser:
    await mplService.assignBarRaiser(contentId, workflowId - 1, authnInfo.userProfile.userId);

    // As I have done my review for the current version, clear the bar raiser status:
    await mplService.deleteBarRaiserStatus(contentId, workflowId - 1);
  };

  const feedbackMessage = (
    <div className="amazon-orange-color-botton-hover">{constants.FEEDBACK_AND_SCORE_SHARED}</div>
  );

  const questionnaireErrorBar =
    showQuestionError && !!questionnaireError ? (
      <Alert statusIconAriaLabel="Error" type="error">
        {questionnaireError}
      </Alert>
    ) : null;

  const reviewCommentSizeErrorBar =
    showError && reviewCommentSizeError ? (
      <Alert statusIconAriaLabel="Error" type="error">
        {reviewCommentSizeError}
      </Alert>
    ) : null;

  const responseCommentSizeErrorBar =
    showError && responseCommentSizeError ? (
      <Alert statusIconAriaLabel="Error" type="error">
        {responseCommentSizeError}
      </Alert>
    ) : null;

  const reviewCommentEmptyErrorBar =
    showError && reviewCommentEmptyError ? (
      <Alert statusIconAriaLabel="Error" type="error">
        {reviewCommentEmptyError}
      </Alert>
    ) : null;

  const submitNoUpdatesReview = async () => {
    setIsProcessing(true);
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };

    try {
      await sendWorkflowEvent(
        WorkflowEventTypes.NO_UPDATES_NEEDED,
        evaluation,
        score,
        workflowId - 1,
      );
      await completeReviewingNonBarRaisedContent();
    } catch (e) {
      console.error(e);
      flashbarMessageBroker.pushErrorMessage('Failed to submit review. Please try again.');
      setIsProcessing(false);
      return;
    }

    handleReviewComplete();
    await navigateToBarRaiserDashboard(true);
    setIsProcessing(false);
  };

  const submitReviewUpdatesNeeded = async () => {
    setIsProcessing(true);
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };

    try {
      // publish wf event
      await sendWorkflowEvent(WorkflowEventTypes.UPDATES_NEEDED, evaluation, score, workflowId - 1);
      // create publication feedback
      await mplService.sendPublicationFeedback(
        contentId,
        PublicationIssueType.BarRaiserUpdateRequest,
        overallComment,
        '',
        undefined,
        true,
      );
      await completeReviewingNonBarRaisedContent();
    } catch (e) {
      console.error(e);
      flashbarMessageBroker.pushErrorMessage('Failed to submit review. Please try again.');
      setIsProcessing(false);
      return;
    }
    handleReviewComplete();
    await navigateToBarRaiserDashboard(true);
    setIsProcessing(false);
  };

  const recommendUnpublish = async () => {
    setIsProcessing(true);
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };

    try {
      await sendWorkflowEvent(
        WorkflowEventTypes.UNPUBLISH_RECOMMENDED,
        evaluation,
        score,
        workflowId - 1,
      );
    } catch (e) {
      flashbarMessageBroker.pushErrorMessage('Failed to submit review. Please try again.');
      setIsProcessing(false);
      return;
    }
    handleReviewComplete();
    await navigateToBarRaiserDashboard(true);
    setIsProcessing(false);
  };

  const confirmUnpublish = async () => {
    setIsProcessing(true);
    const questionnaireResponses = createQuestionnaireResponses(
      idToSequenceMap,
      sequenceToResponseMap,
    );
    const evaluation = { responses: questionnaireResponses };

    try {
      await Promise.all([
        await sendWorkflowEvent(
          WorkflowEventTypes.CONFIRM_UNPUBLISH,
          evaluation,
          score,
          workflowId - 1,
        ),
        await mplService.deleteBarRaiserStatus(contentId, workflowId - 1),
      ]);
    } catch (e) {
      flashbarMessageBroker.pushErrorMessage('Failed to submit review. Please try again.');
      setIsProcessing(false);
      return;
    }
    handleReviewComplete();
    await navigateToBarRaiserDashboard(true);
    setIsProcessing(false);
  };

  const reviewBarRaiserEvaluationActions = (
    <SpaceBetween alignItems="start" size="xs">
      <ModalDialogButtonV3
        header="Confirm unpublishing"
        buttonText="Confirm unpublishing"
        submitButtonText="Confirm"
        text="Are you sure you want to unpublish this content?"
        onSubmit={confirmUnpublish}
        loading={isProcessing}
        fullwidth
      />
      <ModalDialogButtonV3
        header="Submit review - author updates needed"
        onSubmit={submitReviewUpdatesNeeded}
        buttonText="Submit review - author updates needed"
        submitButtonText="Submit"
        text={'Are you sure you want to notify authors updates are needed?'}
        loading={isProcessing}
        fullwidth
      />
    </SpaceBetween>
  );

  const reviewPublishedActions = (
    <SpaceBetween alignItems="start" size="xs">
      <ModalDialogButtonV3
        header="Submit review - no updates needed"
        onSubmit={submitNoUpdatesReview}
        buttonText="Submit review - no updates needed"
        submitButtonText="Submit"
        text={'Are you sure that no new updates are needed for this content?'}
        loading={isProcessing}
        onBeforeOpenModal={validateCompleteReview}
        fullwidth
      />
      <ModalDialogButtonV3
        header="Submit review - author updates needed"
        onSubmit={() => submitReviewUpdatesNeeded()}
        buttonText="Submit review - author updates needed"
        submitButtonText="Submit"
        text={'Are you sure you want to notify authors updates are needed?'}
        loading={isProcessing}
        onBeforeOpenModal={validateCompleteReview}
        fullwidth
      />
      <ModalDialogButtonV3
        header={'Unpublishing recommended'}
        submitButtonText="Confirm"
        buttonText="Unpublishing recommended"
        onSubmit={recommendUnpublish}
        fullwidth
        onBeforeOpenModal={validateCompleteReview}
      >
        Are you sure to recommend unpublishing based on the below comments:
        <br />
        <i>{overallComment}</i>
      </ModalDialogButtonV3>
    </SpaceBetween>
  );

  const reviewDraftActions = (
    <SpaceBetween direction="horizontal" size="xs">
      <ModalDialogButtonV3
        variant="normal"
        header={constants.PUBLISH_ACTION_MODAL_HEADER}
        text={constants.PUBLISH_REJECT_MODAL_CONTENT}
        submitButtonText={constants.REJECT_PUBLICATION_BUTTON_TEXT}
        onBeforeOpenModal={validateReject}
        onSubmit={rejectWorkflow}
        fullwidth
      />
      <ModalDialogButtonV3
        variant="primary"
        header={constants.PUBLISH_ACTION_MODAL_HEADER}
        text={constants.PUBLISH_MODAL_CONTENT}
        submitButtonText={constants.PUBLISH_BUTTON_TEXT}
        onBeforeOpenModal={validateCompleteReview}
        onSubmit={publishWorkflow}
        fullwidth
      />
    </SpaceBetween>
  );

  return (
    <SpaceBetween direction="vertical" size="m">
      {feedbackMessage}
      {questionnaireErrorBar}
      {reviewCommentSizeErrorBar}
      {reviewCommentEmptyErrorBar}
      {responseCommentSizeErrorBar}
      <Box float="left">
        <div>
          {isUnreviewedPublishedContent
            ? reviewPublishedActions
            : props.unpublishRecommended
              ? reviewBarRaiserEvaluationActions
              : reviewDraftActions}
        </div>
      </Box>
    </SpaceBetween>
  );
};

export default withAppContext(BarRaiserControls);
